import { useEffect, useState } from "react";
import { WITH_COOKIE } from "../tokenStorage";
import { AdminNav } from "./AdminPage";

interface UploadedMedia {
  key: string;
  url: string;
  filename: string;
  format: string;
  created_at: string;
}

interface RenderJob {
  status: "running" | "done" | "interrupted";
  total: number;
  done: number;
  failures: { key: string; filename: string; error: string }[];
  finished_at: string | null;
}

interface Render {
  thumbnail: string | null;
  error: string | null;
  rendered_at: string | null;
}

interface AdminTemplatesPageProps {
  apiUrl: string;
  token: string;
  tokenResolved: boolean;
}

async function failure(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body.detail === "string") return body.detail;
  } catch {
    // Not JSON: fall through to the status.
  }
  return `HTTP ${res.status}`;
}

/**
 * Picks which uploaded .embdoc files /api/templates offers, and in what
 * order (app/routers/admin_media.py). Only files uploaded on /admin/media
 * can be picked, so upload there first.
 */
export function AdminTemplatesPage({ apiUrl, token, tokenResolved }: AdminTemplatesPageProps) {
  const [access, setAccess] = useState<"checking" | "denied" | "granted">("checking");
  const [designs, setDesigns] = useState<UploadedMedia[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState<RenderJob | null>(null);
  const [renders, setRenders] = useState<Record<string, Render>>({});

  const post = (path: string, body: object, method = "POST") =>
    fetch(`${apiUrl}/api/admin/media${path}`, {
      ...WITH_COOKIE,
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token || null, ...body }),
    });

  const loadRenders = async () => {
    const res = await post("/templates/render/status", {});
    if (!res.ok) return;
    const body = await res.json();
    setJob(body.job);
    setRenders(body.renders);
  };

  const load = async () => {
    const [media, templates] = await Promise.all([post("/list", {}), post("/templates", {})]);
    if (!media.ok || !templates.ok) return false;
    const all: UploadedMedia[] = (await media.json()).media;
    const keys: string[] = (await templates.json()).keys;
    setDesigns(all.filter((item) => item.format === "embdoc"));
    setSelected(keys);
    setSaved(keys);
    await loadRenders();
    return true;
  };

  // Poll while a job runs; one fetch a second is nothing next to the render.
  useEffect(() => {
    if (job?.status !== "running") return;
    const timer = setTimeout(() => void loadRenders().catch(() => {}), 1000);
    return () => clearTimeout(timer);
  }, [job]);

  const rerender = async () => {
    setError(null);
    try {
      const res = await post("/templates/render", {});
      if (!res.ok) {
        setError(await failure(res));
        return;
      }
      setJob((await res.json()).job);
    } catch (err) {
      setError(`Could not reach the server: ${err}`);
    }
  };

  useEffect(() => {
    if (!tokenResolved) return;
    load()
      .then((ok) => setAccess(ok ? "granted" : "denied"))
      .catch((err) => {
        setError(`Could not reach the server: ${err}`);
        setAccess("denied");
      });
  }, [tokenResolved, token]);

  const toggle = (key: string) =>
    setSelected((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));

  const move = (index: number, by: number) =>
    setSelected((keys) => {
      const next = [...keys];
      [next[index], next[index + by]] = [next[index + by], next[index]];
      return next;
    });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await post("/templates", { keys: selected }, "PUT");
      if (!res.ok) {
        setError(await failure(res));
        return;
      }
      setSaved((await res.json()).keys);
      await loadRenders();
    } catch (err) {
      setError(`Could not reach the server: ${err}`);
    } finally {
      setBusy(false);
    }
  };

  if (access === "checking") return <p aria-busy="true">Loading…</p>;

  if (access === "denied") {
    return (
      <article>
        <h2>Admin</h2>
        <p>{error ?? "This page isn't available."}</p>
      </article>
    );
  }

  const byKey = new Map(designs.map((item) => [item.key, item]));
  const unsaved = selected.join() !== saved.join();

  return (
    <>
      <AdminNav current="templates" />
      <h2>Templates</h2>
      <p>
        The designs <a href={`${apiUrl}/api/templates`}>/api/templates</a> lists, in this order. Pick from the{" "}
        <code>.embdoc</code> files uploaded on <a href="/admin/media">Media</a>.
      </p>

      <h3>Offered</h3>
      {selected.length === 0 ? (
        <p>None yet: tick a design below.</p>
      ) : (
        <ol>
          {selected.map((key, index) => (
            <li key={key}>
              {renders[key]?.thumbnail ? (
                <img
                  src={`${apiUrl}${renders[key].thumbnail}`}
                  alt=""
                  width={48}
                  height={48}
                  style={{ objectFit: "contain", verticalAlign: "middle", marginRight: "0.5rem" }}
                />
              ) : null}
              {byKey.get(key)?.filename ?? key}{" "}
              <button
                type="button"
                className="outline secondary"
                disabled={index === 0}
                aria-label="Move up"
                onClick={() => move(index, -1)}
              >
                ↑
              </button>{" "}
              <button
                type="button"
                className="outline secondary"
                disabled={index === selected.length - 1}
                aria-label="Move down"
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
            </li>
          ))}
        </ol>
      )}
      <button type="button" disabled={busy || !unsaved} aria-busy={busy} onClick={() => void save()}>
        {unsaved ? "Save" : "Saved"}
      </button>
      {error && (
        <p role="alert" style={{ color: "var(--pico-del-color, #b3261e)" }}>
          {error}
        </p>
      )}

      <h3>Thumbnails</h3>
      <p>
        Renders each saved template as a 256 px thumbnail and a 768 px card (WebP and PNG), and a 1200×630 social
        image. Stitch Studio digitizes each design, so this needs it reachable.
      </p>
      <button
        type="button"
        className="secondary"
        disabled={job?.status === "running" || unsaved || saved.length === 0}
        aria-busy={job?.status === "running"}
        onClick={() => void rerender()}
      >
        Rerender thumbnails
      </button>
      {unsaved && <small> Save the list first.</small>}
      {job && (
        <p>
          {job.status === "running" && (
            <>
              Rendering {Math.min(job.done + 1, job.total)} of {job.total}…{" "}
              <progress value={job.done} max={job.total} />
            </>
          )}
          {job.status === "done" &&
            `Last run: ${job.total - job.failures.length} of ${job.total} rendered${
              job.finished_at ? `, ${new Date(job.finished_at).toLocaleString()}` : ""
            }.`}
          {job.status === "interrupted" && "The last run was interrupted by a restart. Run it again."}
        </p>
      )}
      {job && job.failures.length > 0 && (
        <article>
          <strong>Failed</strong> (these keep their previous images):
          <ul>
            {job.failures.map((f) => (
              <li key={f.key}>
                {f.filename}: <code>{f.error}</code>
              </li>
            ))}
          </ul>
        </article>
      )}

      <h3>Uploaded designs</h3>
      {designs.length === 0 ? (
        <p>
          No <code>.embdoc</code> files uploaded yet.
        </p>
      ) : (
        <div className="overflow-auto">
          <table>
            <thead>
              <tr>
                <th>Offer</th>
                <th>File</th>
                <th>Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {designs.map((item) => (
                <tr key={item.key}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Offer ${item.filename}`}
                      checked={selected.includes(item.key)}
                      onChange={() => toggle(item.key)}
                    />
                  </td>
                  <td>
                    <a href={item.url}>{item.filename}</a>
                  </td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
